import { useState } from 'react';
import { useUrlState, useUrlTab } from '@/hooks/useUrlState';
import {
  Stack,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Typography,
} from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Select } from '@liminal/web-ui/forms/Select';
import { IconButton } from '@liminal/web-ui/primitives/IconButton';
import { Pagination } from '@liminal/web-ui/primitives/Pagination';
import { Tabs } from '@liminal/web-ui/primitives/Tabs';
import { useAnnouncements } from '@/hooks/useAnnouncements';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { SortableTableCell } from '@liminal/web-ui/display/SortableTableCell';
import { useTableSort } from '@liminal/web-ui/primitives/useTableSort';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import {
  announcementTypeVariant,
  announcementTypeLabel,
  announcementStatusFromDates,
  pushAnnouncementStatusLabel,
  pushAnnouncementStatusVariant,
} from '@/components/ui/statusHelpers';
import { formatOrderDate } from '@liminal/utils/date';
import type { AdminAnnouncement } from '@/api/types';
import { AnnouncementFormDialog } from '../components/AnnouncementFormDialog';
import { AnnouncementDetailDialog } from '../components/AnnouncementDetailDialog';
import { PushAnnouncementsTab } from '../components/PushAnnouncementsTab';
import { describeAudience } from '../components/describeAudience';
import { fontSizes } from '@liminal/brand/scale';

const PAGE_SIZE = 50;

// Map the Select value to the proto enum string sent to the API
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'ANNOUNCEMENT_STATUS_FILTER_ACTIVE', label: 'Active' },
  { value: 'ANNOUNCEMENT_STATUS_FILTER_EXPIRED', label: 'Expired' },
  { value: 'ANNOUNCEMENT_STATUS_FILTER_SCHEDULED', label: 'Scheduled' },
];

const COLUMN_COUNT = 10;

/**
 * Sorts the page on screen — announcements page by offset and the endpoint
 * takes no sort parameter.
 */
const SORT_ACCESSORS = {
  title: (ann: AdminAnnouncement) => ann.title,
  type: (ann: AdminAnnouncement) => announcementTypeLabel(ann.type),
  priority: (ann: AdminAnnouncement) => ann.priority ?? 0,
  status: (ann: AdminAnnouncement) =>
    announcementStatusFromDates(ann.startsAt, ann.expiresAt).label,
  audience: (ann: AdminAnnouncement) =>
    describeAudience(ann.audienceKind, ann.audienceDescription, ann.audienceSize),
  push: (ann: AdminAnnouncement) =>
    ann.pushAnnouncementId ? pushAnnouncementStatusLabel(ann.pushStatus) : '',
  startsAt: (ann: AdminAnnouncement) => ann.startsAt,
  expiresAt: (ann: AdminAnnouncement) => ann.expiresAt,
  createdAt: (ann: AdminAnnouncement) => ann.createdAt,
};

const ANNOUNCEMENT_TABS = [
  { value: 'in-app', label: 'In-App' },
  { value: 'push', label: 'Push' },
];

const LIST_DEFAULTS = { status: '', offset: 0 };

export function AnnouncementsPage() {
  const [tab, setTab] = useUrlTab(ANNOUNCEMENT_TABS, 'in-app');

  return (
    <Stack spacing={3}>
      <Tabs
        value={tab}
        onChange={setTab}
        aria-label="Announcement channels"
        items={ANNOUNCEMENT_TABS}
      />

      {tab === 'in-app' && <InAppAnnouncementsTab />}
      {tab === 'push' && <PushAnnouncementsTab />}
    </Stack>
  );
}

function InAppAnnouncementsTab() {
  const [{ status: statusFilter, offset }, setParams] = useUrlState(LIST_DEFAULTS);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<AdminAnnouncement | null>(null);
  // The announcement a new one is being seeded from, or null. Separate from
  // `selectedAnnouncement` so a copy opens the form, not the detail view.
  const [copySource, setCopySource] = useState<AdminAnnouncement | null>(null);

  const { data, isLoading, isError } = useAnnouncements({
    status: statusFilter || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const total = data?.total ?? 0;

  // Newest first, as the endpoint returns them.
  const { rows: announcements, sort } = useTableSort(
    data?.announcements ?? [],
    SORT_ACCESSORS,
    'createdAt',
    'desc',
  );

  return (
    <>
      <PageHeader
        title="In-App Announcements"
        autoTitleCase={false}
        actions={
          <Button variant="primary" startIcon="plus" onClick={() => setCreateOpen(true)}>
            Create Announcement
          </Button>
        }
      />
      <Stack spacing={3}>

      <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
        <Select
          variant="filter"
          aria-label="Status"
          size="sm"
          width={180}
          value={statusFilter}
          onChange={(status) => setParams({ status, offset: 0 })}
          options={STATUS_FILTERS}
        />
        {total > 0 && (
          <Typography variant="body2" color="text.secondary">
            {total} announcement{total !== 1 ? 's' : ''}
          </Typography>
        )}
      </Stack>

      <Card variant="elevated" padding="none">
        <TableContainer>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <SortableTableCell column="title" sort={sort}>
                  Title
                </SortableTableCell>
                <SortableTableCell column="type" sort={sort}>
                  Type
                </SortableTableCell>
                <SortableTableCell column="priority" sort={sort}>
                  Priority
                </SortableTableCell>
                <SortableTableCell column="status" sort={sort}>
                  Status
                </SortableTableCell>
                <SortableTableCell column="audience" sort={sort}>
                  Audience
                </SortableTableCell>
                <SortableTableCell column="push" sort={sort}>
                  Push
                </SortableTableCell>
                <SortableTableCell column="startsAt" sort={sort}>
                  Starts At
                </SortableTableCell>
                <SortableTableCell column="expiresAt" sort={sort}>
                  Expires At
                </SortableTableCell>
                <SortableTableCell column="createdAt" sort={sort}>
                  Created
                </SortableTableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={COLUMN_COUNT}
              isLoading={isLoading}
              isError={isError}
              isEmpty={announcements.length === 0}
              loadingRows={8}
              emptyMessage="No announcements found."
              errorMessage="Could not load announcements."
            >
              {announcements.map((ann) => {
                const status = announcementStatusFromDates(ann.startsAt, ann.expiresAt);
                return (
                  <TableRow
                    key={ann.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setSelectedAnnouncement(ann)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {ann.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        label={announcementTypeLabel(ann.type)}
                        tone={announcementTypeVariant(ann.type)}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{ann.priority ?? 0}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusPill label={status.label} tone={status.variant} size="sm" />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 180 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontSize: fontSizes.xs }}
                        title={describeAudience(ann.audienceKind, ann.audienceDescription, ann.audienceSize)}
                      >
                        {describeAudience(ann.audienceKind, ann.audienceDescription, ann.audienceSize)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {ann.pushAnnouncementId ? (
                        <StatusPill
                          label={pushAnnouncementStatusLabel(ann.pushStatus)}
                          tone={pushAnnouncementStatusVariant(ann.pushStatus)}
                          size="sm"
                        />
                      ) : (
                        <Typography variant="body2" sx={{ fontSize: fontSizes.xs }} color="text.secondary">
                          {'\u2014'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                        {formatOrderDate(ann.startsAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                        {ann.expiresAt ? formatOrderDate(ann.expiresAt) : '\u2014'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap', fontSize: fontSizes.xs }}>
                        {formatOrderDate(ann.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {/* The row opens the detail dialog, so the copy has to
                          stop the click before it gets there. */}
                      <IconButton
                        icon="copy"
                        label={`Copy ${ann.title}`}
                        size="sm"
                        tone="muted"
                        onClick={(event) => {
                          event.stopPropagation();
                          setCopySource(ann);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
        </TableContainer>
      </Card>

      <Pagination
        offset={offset}
        pageSize={PAGE_SIZE}
        count={announcements.length}
        total={total}
        onOffsetChange={(next) => setParams({ offset: next })}
        disabled={isLoading}
      />

      <AnnouncementFormDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
      />

      {/* Its own instance rather than a flag on the create dialog: `copyFrom`
          seeds state on open, so the two must not share one mount. */}
      <AnnouncementFormDialog
        open={copySource !== null}
        onClose={() => setCopySource(null)}
        copyFrom={copySource ?? undefined}
      />

      <AnnouncementDetailDialog
        open={selectedAnnouncement !== null}
        onClose={() => setSelectedAnnouncement(null)}
        announcement={selectedAnnouncement}
        onCopy={(ann) => {
          setSelectedAnnouncement(null);
          setCopySource(ann);
        }}
      />
      </Stack>
    </>
  );
}
